<script lang="ts">
/**
 * @module routes/[[locale=locale]]/cv/+page
 * @description Visually styled Curriculum Vitae (CV) page with a rich, print-friendly layout.
 * Displays the portfolio owner's professional profile in four major sections:
 *
 * 1. **Personal Info Card** - Name, title, contact details (email, phone, birthday),
 *    and physical address, rendered in a glassmorphism card with print-safe styling
 * 2. **Education Section** - Timeline of education history with responsive layouts:
 *    a full horizontal timeline on `sm+` screens and a vertical border-left timeline
 *    on mobile. Each entry shows institution, date range badges, and description.
 * 3. **Experience Section** - Work experience in the same dual-timeline layout as education,
 *    with badge-based skills and descriptive text for each position
 * 4. **Skills Section** - Technical and soft skills grouped by category, displayed as
 *    `SkillCard` components within a `MasonryGrid`. Each skill shows proficiency level,
 *    experience duration badges, and an icon/image.
 *
 * Data sources:
 * - `/api/cv` provides translated `education`, `experience`, and `skills` arrays
 * - `$lib/contact` provides structured `contactDetails`
 * - Intlayer dictionaries `cv`, `aboutme`, `sites`, `layout` for i18n
 *
 * The page includes print-specific CSS classes (e.g. `print:bg-white`, `print:text-black`)
 * for clean PDF output via the browser print dialog.
 *
 * Styled CV with dual timeline rendering: horizontal layout on macOS-class
 * viewports (sm+), vertical timeline on iOS-class for better mobile readability.
 *
 * @see {@link $lib/components/timeline-card} for timeline entry cards
 * @see {@link $lib/components/skill-card} for skill display cards
 * @see {@link routes/[[locale=locale]]/cv/+layout.svelte} for shared CV layout with print/toggle buttons
 */

import * as TimelineCard from "$lib/components/timeline-card";
import * as SkillCard from "$lib/components/skill-card";
import * as Card from "$lib/components/ui/card";
import MasonryGrid from "$lib/components/MasonryGrid.svelte";
import { contactDetails } from "$lib/contact";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  LoaderCircle as Loader2,
} from "@lucide/svelte";
import { t, locale } from "$lib/i18n";
import { cn } from "$lib/utils";
import LocalizedLink from "$lib/components/LocalizedLink.svelte";

// --- Types matching /api/cv response ---

type DurationKey = {
  type: string;
  count: number;
};

type EducationItem = {
  key: string;
  image?: string;
  imgAlt: string;
  imageFallback: string;
  name: string;
  startdate: string;
  enddate?: string;
  description: string;
  badges?: string[];
  darkImage?: string;
};

type ExperienceItem = {
  key: string;
  image?: string;
  imgAlt: string;
  imageFallback: string;
  name: string;
  startdate: string;
  enddate?: string;
  description: string;
  badges?: string[];
  darkImage?: string;
};

type SkillItem = {
  title: string;
  category: string;
  badgeKeys: string[];
  staticBadges?: string[];
  translatedBadges?: string[];
  image: string;
  imageAlt: string;
  imageFallback: string;
  level: number;
  darkImage?: string;
  experience?: DurationKey;
};

type SkillCategoryResponse = {
  key: string;
  title: string;
  skills: SkillItem[];
};

type CVResponse = {
  education: EducationItem[];
  experience: ExperienceItem[];
  skills: SkillCategoryResponse[];
};

let { data } = $props();
let cvData = $derived(
  data?.cvData
    ? (data.cvData as unknown as CVResponse)
    : { education: [], experience: [], skills: [] },
);

// --- Helpers ---

function parseDate(d?: string): Date {
  if (!d) return new Date(0);
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? new Date(0) : parsed;
}

// Calculate age
const today = new Date();
const birthday = new Date("2003-06-06");
let age = today.getFullYear() - birthday.getFullYear();
const mo = today.getMonth() - birthday.getMonth();
if (mo < 0 || (mo === 0 && today.getDate() < birthday.getDate())) age--;
</script>

<svelte:head>
  <title>{$t("sites.cv")} | {$t("layout.title")}</title>
</svelte:head>

  <div class={cn("flex flex-col gap-12 px-6 py-10 lg:px-8 mx-auto")}>
    <!-- Personal Info Card -->
    <Card.Root class={cn("my-glass print:bg-white print:shadow-none")}>
      <Card.Header class={cn("text-center pb-2")}>
        <h1 class={cn("text-4xl md:text-5xl font-bold")}>
          {$t("aboutme.name")}
        </h1>
        <p class={cn("text-xl text-muted-foreground")}>
          {$t("cv.about.title")}
        </p>
      </Card.Header>
      <Card.Content>
        <div class={cn("grid grid-cols-1 md:grid-cols-2 gap-6 text-base")}>
          <!-- Contact Info -->
          <div class={cn("flex flex-col gap-2")}>
            <LocalizedLink
              href={contactDetails.email.link}
              class={cn(
                "flex items-center gap-2 hover:text-primary transition-colors",
              )}
            >
              <Mail class={cn("w-4 h-4")} />
              {contactDetails.email.display}
            </LocalizedLink>
            <LocalizedLink
              href={contactDetails.telephone.link}
              class={cn(
                "flex items-center gap-2 hover:text-primary transition-colors",
              )}
            >
              <Phone class={cn("w-4 h-4")} />
              {contactDetails.telephone.display}
            </LocalizedLink>
            <div class={cn("flex items-center gap-2 text-muted-foreground")}>
              <Calendar class={cn("w-4 h-4")} />
              {$t("cv.about.birthday")}: {new Date(
                "2003-06-06",
              ).toLocaleDateString([$locale], {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}
            </div>
          </div>
          <!-- Address -->
          <div class={cn("flex flex-col gap-2")}>
            <LocalizedLink
              href={contactDetails.address.link}
              target="_blank"
              rel="noreferrer"
              class={cn(
                "flex items-start gap-2 hover:text-primary transition-colors",
              )}
            >
              <MapPin class={cn("w-4 h-4 mt-0.5 shrink-0")} />
              <div>
                {contactDetails.address.street}<br />
                {contactDetails.address.zip}
                {$t("cv.about.address.berlin")}, {$t(
                  "cv.about.address.germany",
                )}
              </div>
            </LocalizedLink>
          </div>
        </div>
      </Card.Content>
    </Card.Root>

    <!-- Education Section -->
    <section class={cn("space-y-6")}>
      <h2
        class={cn(
          "text-3xl md:text-4xl font-bold text-center flex items-center justify-center gap-3 mb-6",
        )}
      >
        <svg
          class={cn("w-7 h-7 text-primary print:text-black")}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          ><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path
            d="M6 12v3.5a4 4 0 0 0 4 4h4a4 4 0 0 0 4-4V12"
          /></svg
        >
        {$t("cv.education.title")}
      </h2>

      <div
        class={cn(
          "block relative ml-3 border-l-2 border-border/50 pl-6 space-y-8 pb-4",
        )}
      >
        {#each (cvData?.education || []) as item (item.key)}
          <TimelineCard.Root
            image={item.image || ""}
            imageAlt={item.imgAlt}
            imageFallback={item.imageFallback}
            title={item.name}
            startTime={parseDate(item.startdate)}
            endTime={parseDate(item.enddate)}
            badges={item.badges || []}
          >
            <p class={cn("text-sm text-muted-foreground")}>
              {item.description}
            </p>
          </TimelineCard.Root>
        {/each}
      </div>
    </section>

    <!-- Experience Section -->
    <section class={cn("space-y-6")}>
      <h2
        class={cn(
          "text-3xl md:text-4xl font-bold text-center flex items-center justify-center gap-3 mb-6",
        )}
      >
        <svg
          class={cn("w-7 h-7 text-primary print:text-black")}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          ><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path
            d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"
          /></svg
        >
        {$t("cv.experience.title")}
      </h2>

      <div
        class={cn(
          "block relative ml-3 border-l-2 border-border/50 pl-6 space-y-8 pb-4",
        )}
      >
        {#each (cvData?.experience || []) as item (item.key)}
          <TimelineCard.Root
            image={item.image || ""}
            imageAlt={item.imgAlt}
            imageFallback={item.imageFallback}
            title={item.name}
            startTime={parseDate(item.startdate)}
            endTime={parseDate(item.enddate)}
            badges={item.badges || []}
          >
            <p class={cn("text-sm text-muted-foreground whitespace-pre-line")}>
              {item.description}
            </p>
          </TimelineCard.Root>
        {/each}
      </div>
    </section>

    <!-- Skills Section -->
    <section class={cn("space-y-6")}>
      <h2
        class={cn(
          "text-3xl md:text-4xl font-bold text-center flex items-center justify-center gap-3 mb-6",
        )}
      >
        <svg
          class={cn("w-7 h-7 text-primary print:text-black")}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          ><path
            d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
          /></svg
        >
        {$t("cv.skills.title")}
      </h2>

      <div class={cn("flex flex-col gap-10")}>
        {#each (cvData?.skills || []) as category (category.key)}
          {@const skills = category.skills || []}
          {#if skills.length > 0}
            <div class={cn("break-inside-avoid")}>
              <h3 class={cn("text-2xl font-semibold mb-6 ml-1 opacity-80")}>
                {category.title}
              </h3>
              <div
                class={cn("grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4")}
              >
                {#each category.skills as skill}
                  <SkillCard.Root
                    image={skill.darkImage || skill.image || ""}
                    imagePrint={skill.darkImage ? skill.image : undefined}
                    imageAlt={skill.imageAlt}
                    imageFallback={skill.imageFallback}
                    title={skill.title}
                    level={skill.level}
                    badges={skill.translatedBadges || []}
                    category={skill.category}
                  />
                {/each}
            </div>
            </div>
          {/if}
        {/each}
      </div>
    </section>
  </div>

